import { ref, computed, onMounted, onUnmounted, watch, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAuth0 } from '@auth0/auth0-vue'
import { fetchSSMConfig, getLoggedInBanner, getLoggedOutBanner, isEmergencyRedirectEnabled, getEmergencyRedirectUrl } from '@/api/ssm'

/** Height of the announcement banner in pixels (used for layout offset). */
export const BANNER_HEIGHT_PX = 48

/** Height of the top strip used to prevent Safari from tinting browser chrome (must match #safari-theme-bar in main.css). */
export const SAFARI_STRIP_PX = 20

/** Apply opacity (0–1) to a color string; returns rgba(). Supports hex, rgb(), rgba(), and CSS color names (e.g. "orange"). */
function colorWithOpacity(color: string, opacity: number): string {
  if (opacity >= 1) return color
  const o = Math.max(0, Math.min(1, opacity))
  const hex3 = /^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/
  const hex6 = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/
  const hex8 = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/
  const rgbMatch = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*[\d.]+)?\s*\)$/)
  let r: number, g: number, b: number
  if (hex3.test(color)) {
    const m = color.match(hex3)!
    r = parseInt(m[1] + m[1], 16)
    g = parseInt(m[2] + m[2], 16)
    b = parseInt(m[3] + m[3], 16)
  } else if (hex6.test(color) || hex8.test(color)) {
    const m = color.match(hex6) || color.match(hex8)!
    r = parseInt(m[1], 16)
    g = parseInt(m[2], 16)
    b = parseInt(m[3], 16)
  } else if (rgbMatch) {
    r = parseInt(rgbMatch[1], 10)
    g = parseInt(rgbMatch[2], 10)
    b = parseInt(rgbMatch[3], 10)
  } else {
    /* Resolve CSS color names (e.g. "orange") and other valid CSS colors via the browser */
    const resolved = resolveColorToRgb(color)
    if (resolved) return `rgba(${resolved.r}, ${resolved.g}, ${resolved.b}, ${o})`
    return color
  }
  return `rgba(${r}, ${g}, ${b}, ${o})`
}

/** Resolve any CSS color to r,g,b using a temporary element. Returns null if not parseable. */
function resolveColorToRgb(color: string): { r: number; g: number; b: number } | null {
  if (typeof document === 'undefined') return null
  const el = document.createElement('span')
  el.style.color = color
  el.style.position = 'absolute'
  el.style.opacity = '0'
  document.body.appendChild(el)
  const computed = getComputedStyle(el).color
  document.body.removeChild(el)
  /* Browser may return "rgb(r,g,b)" or "rgba(r,g,b,a)" */
  const m = computed.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
  if (!m) return null
  return {
    r: parseInt(m[1], 10),
    g: parseInt(m[2], 10),
    b: parseInt(m[3], 10)
  }
}

export interface BannerState {
  showBanner: Ref<boolean>
  /** True when banner is visible or during leave transition; use for layout padding/offset so nav doesn't jump */
  reserveBannerSpace: Ref<boolean>
  bannerText: Ref<string>
  /** Optional short text for phone screens; undefined when not configured */
  bannerShortText: Ref<string | undefined>
  bannerBackgroundColor: Ref<string>
  bannerTextColor: Ref<string>
  bannerType: Ref<'hello' | 'info' | 'warning' | 'success' | 'error'>
  showClose: Ref<boolean>
  canClose: Ref<boolean>
  linkUrl: Ref<string | undefined>
  closeBanner: () => void
  bannerAfterLeave: () => void
  bannerHeight: number
  /** Height of the Safari anti-tint strip (px). Banner is positioned below this. */
  stripHeight: number
  /** Total top offset in px (strip + banner when visible). Use for layout padding and nav offset. */
  totalTopOffsetPx: Ref<string>
}

export function useBanner(): BannerState {
  const route = useRoute()
  const { isAuthenticated } = useAuth0()
  
  const showBanner = ref(false)
  const leaving = ref(false)
  const reserveBannerSpace = computed(() => showBanner.value || leaving.value)
  const totalTopOffsetPx = computed(
    () => `${SAFARI_STRIP_PX + (reserveBannerSpace.value ? BANNER_HEIGHT_PX : 0)}px`
  )
  const bannerText = ref('')
  const bannerShortText = ref<string | undefined>(undefined)
  const bannerBackgroundColor = ref('#3b82f6')
  const bannerTextColor = ref('#ffffff')
  const bannerType = ref<'info' | 'warning' | 'error'>('info')
  const showClose = ref(false)
  const canClose = ref(false)
  const linkUrl = ref<string | undefined>(undefined)
  
  /* Dismissed state is in-memory only so the banner shows again on full refresh or new tab */
  const dismissedBannerText = ref<string | null>(null)
  
  let autohideTimeout: ReturnType<typeof setTimeout> | null = null
  
  function clearAutohide() {
    if (autohideTimeout) {
      clearTimeout(autohideTimeout)
      autohideTimeout = null
    }
  }
  
  onMounted(() => {
    checkEmergencyRedirect()
    fetchAndCheckBanner()
  })
  
  /* Re-check banner when route changes; SSM config is refetched in router beforeEach */
  watch(() => route.path, () => {
    fetchAndCheckBanner()
  })
  
  onUnmounted(() => {
    clearAutohide()
  })
  
  function checkEmergencyRedirect() {
    if (isEmergencyRedirectEnabled()) {
      const url = getEmergencyRedirectUrl()
      if (url) {
        // Redirect immediately
        window.location.href = url
      }
    }
  }
  
  function fetchAndCheckBanner() {
    fetchSSMConfig().then(() => {
      const banner = isAuthenticated.value ? getLoggedInBanner() : getLoggedOutBanner()
      
      if (banner && banner.text) {
        const currentText = banner.text.trim()
        if (dismissedBannerText.value === currentText) {
          /* User already dismissed (or autohide hid) this message this session; don't show until message changes */
          if (showBanner.value) {
            leaving.value = true
            showBanner.value = false
          }
          return
        }
        showBanner.value = true
        bannerText.value = banner.text
        bannerShortText.value = banner.shortText?.trim() || undefined
        const bgColor = banner.color ?? getBannerColor(banner.type || 'info', 'bg')
        const txtColor = banner.textColor ?? getBannerColor(banner.type || 'info', 'text')
        bannerBackgroundColor.value =
          banner.opacity != null
            ? colorWithOpacity(bgColor, banner.opacity)
            : bgColor
        bannerTextColor.value =
          banner.textOpacity != null
            ? colorWithOpacity(txtColor, banner.textOpacity)
            : txtColor
        bannerType.value = (banner.type as 'info' | 'warning' | 'error') || 'info'
        showClose.value = banner.close ?? true
        canClose.value = banner.close ?? true
        linkUrl.value = banner.linkUrl
        
        /* Auto-hide after N seconds if configured */
        clearAutohide()
        const seconds = banner.autoHide
        if (typeof seconds === 'number' && seconds > 0) {
          autohideTimeout = setTimeout(() => {
            autohideTimeout = null
            dismissedBannerText.value = bannerText.value.trim()
            leaving.value = true
            showBanner.value = false
          }, seconds * 1000)
        }
      } else {
        clearAutohide()
        if (showBanner.value) leaving.value = true
        showBanner.value = false
      }
    })
  }
  
  function getBannerColor(type: 'info' | 'warning' | 'error', mode: 'bg' | 'text'): string {
    switch (type) {
      case 'error':
        return mode === 'bg' ? '#ef4444' : '#ffffff'
      case 'warning':
        return mode === 'bg' ? '#f59e0b' : '#ffffff'
      case 'info':
      default:
        return mode === 'bg' ? '#3b82f6' : '#ffffff'
    }
  }
  
  function closeBanner() {
    clearAutohide()
    dismissedBannerText.value = bannerText.value.trim()
    leaving.value = true
    showBanner.value = false
  }
  
  function bannerAfterLeave() {
    leaving.value = false
  }
  
  return {
    showBanner,
    reserveBannerSpace,
    bannerText,
    bannerShortText,
    bannerBackgroundColor,
    bannerTextColor,
    bannerType,
    showClose,
    canClose,
    linkUrl,
    closeBanner,
    bannerAfterLeave,
    bannerHeight: BANNER_HEIGHT_PX,
    stripHeight: SAFARI_STRIP_PX,
    totalTopOffsetPx
  }
}